"use client";

import { CheckIcon, ChevronDownIcon } from "@radix-ui/react-icons";
import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

interface SelectContextValue {
  value: string;
  onValueChange: (value: string) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  disabled: boolean;
}

const SelectContext = React.createContext<SelectContextValue | null>(null);

const useSelectContext = () => {
  const context = React.useContext(SelectContext);
  if (!context) {
    throw new Error("Select components must be used within a Select");
  }
  return context;
};

interface SelectProps {
  value?: string;
  onValueChange?: (value: string) => void;
  children: React.ReactNode;
  disabled?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const Select = ({
  value = "",
  onValueChange,
  children,
  disabled = false,
  open: controlledOpen,
  onOpenChange,
}: SelectProps) => {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const handleValueChange = (newValue: string) => {
    onValueChange?.(newValue);
    // Use setTimeout to ensure the value change is processed before closing
    setTimeout(() => setOpen(false), 0);
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    if (!open || !triggerRef.current) return;

    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target as Node)) {
        // Check if the click is on the dropdown content
        const dropdownElement = document.querySelector('[data-select-content="true"]');
        if (dropdownElement?.contains(e.target as Node)) {
          return; // Don't close if clicking inside dropdown
        }
        setOpen(false);
      }
    };

    // Add a small delay to avoid immediate closure on mobile
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [open, setOpen]);

  // Removed handleClickOutside - now handled by overlay in SelectContent

  const contextValue: SelectContextValue = {
    value,
    onValueChange: handleValueChange,
    open,
    setOpen: disabled ? () => {} : setOpen,
    triggerRef,
    disabled,
  };

  return <SelectContext.Provider value={contextValue}>{children}</SelectContext.Provider>;
};

interface SelectTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  dataTestId?: string;
}

const SelectTrigger = React.forwardRef<HTMLButtonElement, SelectTriggerProps>(
  ({ className, children, onClick, dataTestId, ...props }, ref) => {
    const { open, setOpen, triggerRef, disabled } = useSelectContext();

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setOpen(!open);
      onClick?.(e);
    };

    return (
      <button
        ref={(node) => {
          triggerRef.current = node;
          if (typeof ref === "function") ref(node);
          else if (ref) ref.current = node;
        }}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        data-test-id={dataTestId}
        disabled={disabled}
        className={cn(
          "flex h-11 md:h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-base md:text-sm shadow-sm ring-offset-background data-[placeholder]:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
          className
        )}
        onClick={handleClick}
        {...props}
      >
        {children}
        <ChevronDownIcon className="h-4 w-4 opacity-50" />
      </button>
    );
  }
);
SelectTrigger.displayName = "SelectTrigger";

interface SelectValueProps {
  placeholder?: string;
  children?: React.ReactNode;
}

const SelectValue = ({ placeholder, children }: SelectValueProps) => {
  const { value } = useSelectContext();

  if (children) {
    return <>{children}</>;
  }

  return <span>{value || placeholder}</span>;
};

interface SelectContentProps {
  children: React.ReactNode;
  className?: string;
}

const SelectContent = ({ children, className }: SelectContentProps) => {
  const { open, triggerRef } = useSelectContext();
  const [position, setPosition] = React.useState({
    top: 0,
    left: 0,
    width: 0,
    maxHeight: 300,
  });
  const contentRef = React.useRef<HTMLDivElement>(null);

  // Detect if we're inside a dialog or sheet
  const [portalContainer, setPortalContainer] = React.useState<Element | null>(null);
  const [isInModal, setIsInModal] = React.useState(false);

  React.useEffect(() => {
    if (!triggerRef.current) return;

    // Look for parent dialog or sheet container using multiple selectors
    const dialogContainer = triggerRef.current.closest(
      "[data-radix-dialog-content], [data-radix-sheet-content], " +
        '[role="dialog"], [role="alertdialog"], ' +
        ".dialog-content, .sheet-content, " +
        '[data-state="open"][data-radix-collection-item]'
    );

    // Also check for parent with high z-index that might be a modal
    let currentElement = triggerRef.current.parentElement;
    let foundModal = dialogContainer;

    while (currentElement && !foundModal) {
      const computedStyle = window.getComputedStyle(currentElement);
      const zIndex = Number.parseInt(computedStyle.zIndex, 10);

      // If we find an element with high z-index and fixed/absolute positioning, it's likely a modal
      if (zIndex >= 50 && ["fixed", "absolute"].includes(computedStyle.position)) {
        foundModal = currentElement;
        break;
      }
      currentElement = currentElement.parentElement;
    }

    if (foundModal) {
      setPortalContainer(foundModal);
      setIsInModal(true);
    } else {
      setPortalContainer(document.body);
      setIsInModal(false);
    }
  }, [triggerRef]);

  React.useEffect(() => {
    if (!open || !triggerRef.current || !portalContainer) return;

    const trigger = triggerRef.current;
    const triggerRect = trigger.getBoundingClientRect();

    // Get the portal container's position for offset calculation
    const containerRect =
      portalContainer === document.body
        ? {
            left: 0,
            top: 0,
            width: window.innerWidth,
            height: window.innerHeight,
          }
        : (portalContainer as Element).getBoundingClientRect();

    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    // Calculate content dimensions (estimate)
    const contentWidth = Math.max(triggerRect.width, 120); // minimum 120px width for time selectors
    const contentMaxHeight = 300;
    const spacing = 8; // spacing from screen edges

    // Calculate positions relative to the portal container
    let left, top;

    if (isInModal) {
      // When in modal, calculate position relative to the modal container
      left = triggerRect.left - containerRect.left;
      top = triggerRect.bottom - containerRect.top + 4;

      // Ensure dropdown fits within modal bounds
      const modalWidth = containerRect.width;
      const modalHeight = containerRect.height;

      // Adjust horizontal position if it would overflow modal
      if (left + contentWidth > modalWidth - spacing) {
        left = modalWidth - contentWidth - spacing;
      }
      if (left < spacing) {
        left = spacing;
      }

      // Adjust vertical position and height if it would overflow modal
      const spaceBelow = modalHeight - (triggerRect.bottom - containerRect.top) - spacing;
      const spaceAbove = triggerRect.top - containerRect.top - spacing;

      let maxHeight = contentMaxHeight;

      if (spaceBelow < contentMaxHeight && spaceAbove > spaceBelow) {
        // Position above trigger if more space available
        top = triggerRect.top - containerRect.top - 4;
        maxHeight = Math.min(contentMaxHeight, spaceAbove);
        // Use transform to position above
        if (contentRef.current) {
          contentRef.current.style.transform = "translateY(-100%)";
        }
      } else if (spaceBelow < contentMaxHeight) {
        // Limit height to available space below
        maxHeight = Math.max(150, spaceBelow);
      }

      setPosition({
        top,
        left,
        width: Math.max(triggerRect.width, 120),
        maxHeight,
      });
    } else {
      // Original viewport-based positioning for document.body portal
      // Calculate horizontal position - prefer right alignment
      left = triggerRect.right + window.scrollX - contentWidth;

      // Check if content would overflow left edge
      if (left < spacing + window.scrollX) {
        // If can't fit right-aligned, try left-aligned from trigger start
        left = triggerRect.left + window.scrollX;

        // If still overflows right edge, push back to fit
        if (left + contentWidth > viewport.width - spacing) {
          left = viewport.width - contentWidth - spacing + window.scrollX;
        }
      }

      // Final safety check - ensure it doesn't go off left edge
      if (left < spacing + window.scrollX) {
        left = spacing + window.scrollX;
      }

      // Calculate vertical position
      top = triggerRect.bottom + window.scrollY + 4;
      let maxHeight = contentMaxHeight;

      // Check if content would overflow bottom edge
      const spaceBelow = viewport.height - triggerRect.bottom - spacing;
      const spaceAbove = triggerRect.top - spacing;

      if (spaceBelow < contentMaxHeight && spaceAbove > spaceBelow) {
        // Position above trigger if more space available
        top = triggerRect.top + window.scrollY - 4;
        maxHeight = Math.min(contentMaxHeight, spaceAbove);
        // Use transform to position above
        if (contentRef.current) {
          contentRef.current.style.transform = "translateY(-100%)";
        }
      } else if (spaceBelow < contentMaxHeight) {
        // Limit height to available space below
        maxHeight = Math.max(150, spaceBelow); // minimum 150px height
      }

      setPosition({
        top,
        left,
        width: Math.max(triggerRect.width, 120), // minimum 120px to match trigger
        maxHeight,
      });
    }
  }, [open, triggerRef, portalContainer, isInModal]);

  if (!open || !portalContainer) return null;

  // Use different z-index values based on context
  const contentZIndex = isInModal ? 20 : 1000000;
  const listboxZIndex = isInModal ? 30 : 1000001;

  return createPortal(
    <>
      {/* Dropdown content */}
      <div
        ref={contentRef}
        data-select-content="true"
        className={cn(
          "absolute overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95",
          className
        )}
        style={{
          zIndex: contentZIndex,
          top: position.top,
          left: position.left,
          minWidth: position.width,
          maxWidth: isInModal
            ? `${(portalContainer as Element)?.getBoundingClientRect().width - 16}px`
            : `calc(100vw - 16px)`,
          // Ensure dropdown content can receive pointer events
          pointerEvents: "auto",
        }}
        onMouseDown={(e) => {
          // Prevent the overlay from closing when clicking inside the dropdown
          e.stopPropagation();
        }}
        onTouchStart={(e) => {
          // Prevent the overlay from closing when touching inside the dropdown
          e.stopPropagation();
        }}
      >
        <div
          role="listbox"
          className="overflow-y-auto relative"
          style={{
            zIndex: listboxZIndex,
            maxHeight: position.maxHeight,
            userSelect: "none",
            WebkitUserSelect: "none",
            touchAction: "manipulation",
          }}
        >
          {children}
        </div>
      </div>
    </>,
    portalContainer
  );
};

interface SelectItemProps {
  value: string;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
}

const SelectItem = ({ value, children, className, disabled = false }: SelectItemProps) => {
  const { value: selectedValue, onValueChange } = useSelectContext();
  const isSelected = value === selectedValue;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      onValueChange(value);
    }
  };

  return (
    <div
      role="option"
      aria-selected={isSelected}
      data-value={value}
      data-radix-select-item
      className={cn(
        "relative flex w-full cursor-default select-none items-center rounded-sm py-2.5 md:py-1.5 pl-3 md:pl-2 pr-8 text-base md:text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
        disabled && "pointer-events-none opacity-50",
        isSelected && "bg-accent text-accent-foreground",
        className
      )}
      style={{
        zIndex: 1, // Relative z-index within the dropdown
        userSelect: "none",
        WebkitUserSelect: "none",
        touchAction: "manipulation",
      }}
      onClick={handleClick}
    >
      {isSelected && (
        <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
          <CheckIcon className="h-4 w-4" />
        </span>
      )}
      {children}
    </div>
  );
};

const SelectSeparator = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("-mx-1 my-1 h-px bg-muted", className)} {...props} />
  )
);
SelectSeparator.displayName = "SelectSeparator";

export { Select, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectSeparator };
